class AlterationsForm extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    this._isProcessing = false;

    this.families = ['sleeve', 'length'];
    this.familyConfig = {
      sleeve: {
        label: 'Rękaw',
        keys: { shorten: 'sleeveShorten', lengthen: 'sleeveLengthen' },
      },
      length: {
        label: 'Długość',
        keys: { shorten: 'coatShorten', lengthen: 'coatLengthen' },
      },
    };
    this.directionLabels = { shorten: 'Skrócenie', lengthen: 'Wydłużenie' };

    this.prices = {
      sleeveShorten: this.dataset.sleeveShortenPrice,
      sleeveLengthen: this.dataset.sleeveLengthenPrice,
      coatShorten: this.dataset.coatShortenPrice,
      coatLengthen: this.dataset.coatLengthenPrice,
    };
    this.variantIds = {
      sleeveShorten: this.dataset.sleeveShortenVariantId,
      sleeveLengthen: this.dataset.sleeveLengthenVariantId,
      coatShorten: this.dataset.coatShortenVariantId,
      coatLengthen: this.dataset.coatLengthenVariantId,
    };

    this.consentCheckbox = this.querySelector('input[type="checkbox"]');
    this.priceHint = this.querySelector('[data-alterations-price-hint]');
    this.defaultHintText = this.priceHint ? this.priceHint.textContent : '';

    this.disclosureEl = this.querySelector('accordion-disclosure');
    this.detailsEl = this.querySelector('details');

    // Shopify's native dynamic checkout button (Apple Pay / Shop Pay / "Kup teraz") bypasses
    // the theme's ProductForm entirely and never fires "variant:add", so addon line items
    // would silently be skipped. We hide it while an alteration is selected to force the
    // regular add-to-cart flow, which we do control.
    this.dynamicCheckoutWrapper = document.querySelector(`buy-buttons[form="${this.formId}"]`);

    this.familyEls = {};
    this.families.forEach((family) => {
      const wrapper = this.querySelector(`[data-alterations-family="${family}"]`);
      if (!wrapper) return;
      this.familyEls[family] = {
        wrapper,
        radios: Array.from(wrapper.querySelectorAll('input[type="radio"]')),
        cmInput: wrapper.querySelector('[data-alterations-cm-input]'),
        fieldError: wrapper.querySelector('[data-alterations-field-error]'),
        hidden: this.querySelector(`[data-alterations-hidden="${family}"]`),
      };
    });

    this.onRadioChange = this.onRadioChange.bind(this);
    this.onCmInput = this.onCmInput.bind(this);
    this.onVariantAdd = this.onVariantAdd.bind(this);
    this.onDetailsToggle = this.onDetailsToggle.bind(this);

    Object.values(this.familyEls).forEach(({ radios, cmInput }) => {
      radios.forEach((radio) => radio.addEventListener('change', this.onRadioChange));
      if (cmInput) cmInput.addEventListener('input', this.onCmInput);
    });

    // The submit happens inside <product-form>, not this element, so we listen on
    // document.documentElement where the theme's ProductForm bubbles "variant:add" to.
    document.documentElement.addEventListener('variant:add', this.onVariantAdd);

    // Native "toggle" fires whenever <details> opens or closes, whether triggered by the
    // shopper clicking the summary or by us calling disclosureEl.close() programmatically.
    if (this.detailsEl) this.detailsEl.addEventListener('toggle', this.onDetailsToggle);
  }

  disconnectedCallback() {
    Object.values(this.familyEls).forEach(({ radios, cmInput }) => {
      radios.forEach((radio) => radio.removeEventListener('change', this.onRadioChange));
      if (cmInput) cmInput.removeEventListener('input', this.onCmInput);
    });
    document.documentElement.removeEventListener('variant:add', this.onVariantAdd);
    if (this.detailsEl) this.detailsEl.removeEventListener('toggle', this.onDetailsToggle);
  }

  onDetailsToggle() {
    if (!this.detailsEl.open) this.resetAdjustments();
  }

  getFamilyOf(el) {
    return this.families.find((family) => {
      const state = this.familyEls[family];
      return state && (state.radios.includes(el) || state.cmInput === el);
    });
  }

  getDirection(family) {
    const state = this.familyEls[family];
    if (!state) return null;
    const checked = state.radios.find((radio) => radio.checked);
    return checked ? checked.value : null;
  }

  getCm(family) {
    const state = this.familyEls[family];
    if (!state || !state.cmInput) return 0;
    return Number(state.cmInput.value) || 0;
  }

  priceKey(family, direction) {
    const config = this.familyConfig[family];
    return config ? config.keys[direction] : null;
  }

  // A family is "active" only when a direction is picked AND cm > 0.
  isFamilyActive(family) {
    const direction = this.getDirection(family);
    return Boolean(direction) && this.getCm(family) > 0;
  }

  // A family with a direction chosen but no cm entered blocks only that family's addon.
  getPendingFamilies() {
    return this.families.filter((family) => {
      const direction = this.getDirection(family);
      return direction && this.getCm(family) <= 0;
    });
  }

  hasActiveAdjustment() {
    return this.families.some((family) => this.isFamilyActive(family));
  }

  // Any direction picked (even before cm is filled in) is enough to require the regular
  // add-to-cart flow, since that's the earliest point the shopper commits to an addon.
  hasSelection() {
    return this.families.some((family) => Boolean(this.getDirection(family)));
  }

  updateDynamicCheckoutVisibility() {
    if (!this.dynamicCheckoutWrapper) return;
    this.dynamicCheckoutWrapper.classList.toggle('buy-buttons--alterations-active', this.hasSelection());
  }

  // Returns the addon line entries implied by the current selections (0-2, one per active family).
  getAddonLines() {
    const lines = [];
    this.families.forEach((family) => {
      if (!this.isFamilyActive(family)) return;
      const direction = this.getDirection(family);
      const key = this.priceKey(family, direction);
      lines.push({ variantId: this.variantIds[key], price: this.prices[key] });
    });
    return lines;
  }

  onRadioChange(event) {
    const family = this.getFamilyOf(event.target);
    if (!family) return;
    const state = this.familyEls[family];
    const direction = this.getDirection(family);

    if (state.cmInput) {
      state.cmInput.disabled = !direction;
      // Disabled fields are excluded from constraint validation, so this only bites once a
      // direction is actually picked — that's what makes the native form.checkValidity()
      // (already called by the theme before every add-to-cart submit) block an empty cm.
      state.cmInput.required = Boolean(direction);
      if (!direction) state.cmInput.value = '';
    }

    this.clearFieldError(family);
    this.syncConsent();
    this.updateHiddenProperties();
    this.updatePriceHint();
    this.updateDynamicCheckoutVisibility();
  }

  onCmInput(event) {
    const family = this.getFamilyOf(event.target);
    if (family) this.clearFieldError(family);
    this.syncConsent();
    this.updateHiddenProperties();
    this.updatePriceHint();
  }

  updateHiddenProperties() {
    this.families.forEach((family) => {
      const state = this.familyEls[family];
      if (!state || !state.hidden) return;
      if (this.isFamilyActive(family)) {
        const direction = this.getDirection(family);
        state.hidden.value = `${this.directionLabels[direction]} o ${this.getCm(family)} cm`;
      } else {
        state.hidden.value = '';
      }
    });
  }

  updatePriceHint() {
    if (!this.priceHint) return;

    const lines = this.getAddonLines();
    if (lines.length === 0) {
      this.priceHint.textContent = this.defaultHintText;
      return;
    }

    const priceList = lines.map((line) => line.price).filter(Boolean).join(' + ');
    this.priceHint.textContent = `${this.defaultHintText} Do zapłaty za poprawkę: ${priceList}.`;
  }

  syncConsent() {
    if (!this.consentCheckbox) return;
    if (this.hasActiveAdjustment()) {
      this.consentCheckbox.required = true;
    } else {
      this.consentCheckbox.required = false;
      this.consentCheckbox.checked = false;
    }
  }

  clearFieldError(family) {
    const state = this.familyEls[family];
    if (!state) return;
    if (state.cmInput) state.cmInput.setCustomValidity('');
    if (state.fieldError) {
      state.fieldError.hidden = true;
      state.fieldError.textContent = '';
    }
  }

  showFieldError(family) {
    const state = this.familyEls[family];
    if (!state) return;
    const config = this.familyConfig[family];
    const message = `Podaj liczbę centymetrów dla: ${config.label}.`;

    if (state.cmInput) {
      state.cmInput.setCustomValidity(message);
      state.cmInput.reportValidity();
    }
    if (state.fieldError) {
      state.fieldError.hidden = false;
      state.fieldError.textContent = message;
    }
  }

  async onVariantAdd(event) {
    if (this._isProcessing) return;

    const form = event.target.closest('form');
    // form.id is shadowed by Shopify's hidden <input name="id"> (variant id) inside the
    // form, so the DOM id must be read via getAttribute instead of the .id property.
    if (!form || form.getAttribute('id') !== this.formId) return;

    const pendingFamilies = this.getPendingFamilies();
    pendingFamilies.forEach((family) => this.showFieldError(family));

    if (!this.hasActiveAdjustment()) return;

    const lines = this.getAddonLines();
    const summary = this.buildSummary();
    const items = lines
      .map((line) => line.variantId)
      .filter(Boolean)
      .map((variantId) => ({
        id: variantId,
        quantity: 1,
        properties: { '_Powiązane zamówienie': summary },
      }));

    if (items.length === 0) return;

    this._isProcessing = true;

    try {
      // keepalive lets this request survive a navigation (e.g. the shopper clicking
      // through to checkout right after add-to-cart), which would otherwise abort it.
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
        keepalive: true,
      });

      if (!response.ok) {
        this.dispatchAddonError(form);
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      this.resetAdjustments();
      this.disclosureEl?.close();
    } catch (error) {
      this.dispatchAddonError(form);
    } finally {
      this._isProcessing = false;
    }
  }

  buildSummary() {
    return this.families
      .filter((family) => this.isFamilyActive(family))
      .map((family) => {
        const config = this.familyConfig[family];
        const direction = this.getDirection(family);
        return `${config.label}: ${this.directionLabels[direction]} o ${this.getCm(family)} cm`;
      })
      .join(', ');
  }

  dispatchAddonError(form) {
    // theme.js's own cart:error listeners (BuyButtons, QuickOrderListQuantitySelector) are
    // attached to the form/element itself, not to document, so we bubble from the form
    // to actually reach them instead of dispatching on document (which nothing listens to).
    const target = form || document;
    target.dispatchEvent(new CustomEvent('cart:error', {
      bubbles: true,
      detail: { message: 'Nie udało się dodać opłaty za poprawkę. Spróbuj ponownie.' },
    }));
  }

  resetAdjustments() {
    this.families.forEach((family) => {
      const state = this.familyEls[family];
      if (!state) return;
      state.radios.forEach((radio) => {
        radio.checked = false;
      });
      if (state.cmInput) {
        state.cmInput.value = '';
        state.cmInput.disabled = true;
        state.cmInput.required = false;
        state.cmInput.setCustomValidity('');
      }
      if (state.hidden) state.hidden.value = '';
      if (state.fieldError) {
        state.fieldError.hidden = true;
        state.fieldError.textContent = '';
      }
    });

    if (this.consentCheckbox) {
      this.consentCheckbox.required = false;
      this.consentCheckbox.checked = false;
    }

    this.updatePriceHint();
    this.updateDynamicCheckoutVisibility();
  }
}

if (!window.customElements.get('alterations-form')) {
  window.customElements.define('alterations-form', AlterationsForm);
}
